import { Component, Input, OnInit, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BlockUIModule } from 'primeng/blockui';
import { UtilityService } from '../../../../services/utility/utility.service';
import { DropdownModule } from 'primeng/dropdown';
import { validateFile } from '../../../../direct-apply/shared/validators/form-validation.utils';

export interface UploadedFileItem {
  file?: File;
  previewUrl: string | SafeResourceUrl;
  name: string;
  type?: string;
  base64Data?: string;
}

@Component({
  selector: 'app-experiences',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputTextareaModule,
    CalendarModule,
    FileUploadModule,
    ButtonModule,
    DialogModule,
    SelectButtonModule,
    DropdownModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    BlockUIModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './experiences.component.html',
  styleUrls: ['./experiences.component.scss']
})
export class ExperiencesComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Output() stepSaved = new EventEmitter<void>();
  @Output() saveFailed = new EventEmitter<void>();
  @Output() stepStatusChanged = new EventEmitter<boolean>();

  experiencesForm!: FormGroup;

  @ViewChild('hiddenFileInput') hiddenFileInput!: ElementRef<HTMLInputElement>;
  currentUploadIndex: number = -1;
  displayPreviewDialog: boolean = false;
  selectedPreviewItem: UploadedFileItem | null = null;
  safePdfUrl: SafeResourceUrl | null = null;

  existingIds: (number | null)[] = [];
  savingStates: { saving: boolean }[] = [];
  savingInProgress: boolean = false;
  countryOptions: { label: string; value: string }[] = [];
  isLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private onboardingService: OnboardingService,
    private utilityService: UtilityService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit() {
    this.experiencesForm = this.fb.group({
      wantsToShare: [true],
      entries: this.fb.array([])
    });

    this.parentForm.setControl('experiences', this.experiencesForm);
    this.entries.valueChanges.subscribe(() => this.checkStepStatus());


    this.experiencesForm.get('wantsToShare')?.valueChanges.subscribe(wants => {
      if (wants === true) {
        if (this.entries.length === 0) this.addExperience();
      } else if (wants === false) {
        this.entries.clear();
        this.existingIds = [];
        this.savingStates = [];
      }
    });

    this.isLoading = true;

    forkJoin({
      countries: this.utilityService.getCountries().pipe(catchError(() => of({ result: [] }))),
      experiences: this.onboardingService.getExperiences().pipe(catchError(() => of([])))
    }).subscribe({
      next: (res) => {
        if (res.countries.result) {
          this.countryOptions = res.countries.result.map((c: any) => ({ label: c.name, value: c.name }));
        }
        this.handleExistingData(res.experiences);
        if (this.entries.length === 0) this.addExperience();
        this.isLoading = false;
        this.checkStepStatus();
      },
      error: () => {
        this.isLoading = false;
        this.checkStepStatus();
      }
    });
  }

  handleExistingData(res: any) {
    const list = Array.isArray(res) ? res : (res?.result || []);

    if (list.length > 0) {
      this.experiencesForm.get('wantsToShare')?.setValue(true, { emitEvent: false });
      list.forEach((entry: any) => {
        const id = entry.id ?? entry.Id;
        this.existingIds.push(id || null);
        this.savingStates.push({ saving: false });

        const group = this.createExperience();
        group.patchValue({
          organisationName: entry.organisationName || '',
          from: entry.from ? new Date(entry.from) : null,
          to: entry.to ? new Date(entry.to) : null,
          designation: entry.designation || '',
          city: entry.city || '',
          country: entry.country || '',
          responsibilities: entry.keyResponsibilities || ''
        });

        const docUrl = entry.experienceOrCVLetterUrl;
        if (docUrl) {
          const urlWithoutParams = docUrl.split('?')[0];
          const fileName = urlWithoutParams.split('/').pop() || 'document';
          const type = urlWithoutParams.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
          group.get('cvFiles')?.setValue([{ name: fileName, previewUrl: docUrl, type, base64Data: docUrl }]);
          group.get('cvFiles')?.setErrors(null);
        }

        this.entries.push(group);
      });
    }
  }

  checkStepStatus() {
    const wantsToShare = this.experiencesForm.get('wantsToShare')?.value;
    if (wantsToShare !== true) {
      this.stepStatusChanged.emit(true);
      return;
    }
    const allSaved = this.entries.length > 0 &&
                     this.existingIds.every(id => id !== null) &&
                     this.entries.controls.every(c => !c.dirty);
    this.stepStatusChanged.emit(allSaved);
  }

  get entries(): FormArray {
    return this.experiencesForm.get('entries') as FormArray;
  }

  createExperience(): FormGroup {
    return this.fb.group({
      organisationName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      from: [null, Validators.required],
      to: [null, Validators.required],
      designation: ['', [Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      city: ['', [Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      country: ['', Validators.required],
      responsibilities: ['', Validators.required],
      cvFiles: [[]]
    });
  }

  addExperience() {
    this.entries.push(this.createExperience());
    this.existingIds.push(null);
    this.savingStates.push({ saving: false });
    this.checkStepStatus();
  }

  removeExperience(index: number) {
    const id = this.existingIds[index];

    const doRemoveInUI = () => {
      if (this.entries.length > 1) {
        this.entries.removeAt(index);
        this.existingIds.splice(index, 1);
        this.savingStates.splice(index, 1);
      } else {
        this.entries.at(0).reset();
        this.existingIds[0] = null;
        this.savingStates[0] = { saving: false };
      }
      this.checkStepStatus();
    };

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this experience record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-primary custom-confirm-yes',
      rejectButtonStyleClass: 'p-button-primary custom-confirm-no',
      accept: () => {
        if (id) {
          this.onboardingService.deleteExperience(id).subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Experience record removed.' });
              doRemoveInUI();
            },
            error: () => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete record from server.' });
            }
          });
        } else {
          doRemoveInUI();
        }
      }
    });
  }

  extractDocUrl(controlValue: any[]): string | null {
    if (!controlValue || controlValue.length === 0) return null;
    const item = controlValue[0];
    return (typeof item.previewUrl === 'string' ? item.previewUrl : null) || item.base64Data || null;
  }

  buildFormData(vals: any, hasExperience: boolean = true): FormData {
    const fd = new FormData();
    fd.append('HasExperienceDetails', hasExperience ? 'true' : 'false');
    fd.append('OrganisationName', vals.organisationName || '');
    fd.append('From', vals.from ? new Date(vals.from).toISOString() : '');
    fd.append('To', vals.to ? new Date(vals.to).toISOString() : '');
    fd.append('Designation', vals.designation || '');
    fd.append('City', vals.city || '');
    fd.append('Country', vals.country || '');
    fd.append('KeyResponsibilities', vals.responsibilities || '');

    const files: UploadedFileItem[] = Array.isArray(vals.cvFiles) ? vals.cvFiles : [];
    if (files.length === 0) {
      fd.append('RemoveDocument', 'true');
    } else if (files.length > 0 && files[0].file instanceof File) {
      fd.append('ExperienceOrCVLetterUrl', files[0].file, files[0].name);
    }

    return fd;
  }

  buildPayload(vals: any, hasExperience: boolean = true): any {
    return {
      HasExperienceDetails: hasExperience,
      OrganisationName: vals.organisationName,
      From: vals.from ? new Date(vals.from).toISOString() : null,
      To: vals.to ? new Date(vals.to).toISOString() : null,
      Designation: vals.designation,
      City: vals.city,
      Country: vals.country,
      KeyResponsibilities: vals.responsibilities,
      ExperienceOrCVLetterUrl: this.extractDocUrl(vals.cvFiles)
    };
  }

  saveEntry(index: number) {
    void this.persistEntry(index);
  }

  private persistEntry(index: number): Promise<boolean> {
    const group = this.entries.at(index);
    if (!group) return Promise.resolve(false);

    // If it's already saved and NOT dirty, don't hit the API again
    const existingId = this.existingIds[index];
    if (existingId && !group.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return Promise.resolve(true);
    }

    group.markAllAsTouched();
    if (group.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 5000 });
      return Promise.resolve(false);
    }

    if (this.savingStates[index]) this.savingStates[index].saving = true;
    this.isLoading = true;

    const vals = group.value;
    const formData = this.buildFormData(vals);

    if (existingId) {
      formData.append('Id', existingId.toString());

      return new Promise((resolve) => this.onboardingService.updateExperience(existingId, formData).subscribe({
        next: (res: any) => {
          const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;
          if (newId) this.existingIds[index] = newId;
          group.markAsPristine();
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          this.checkStepStatus();
          resolve(true);
        },
        error: (err) => {
          console.error('Update failed', err);
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          resolve(false);
        }
      }));
    } else {
      return new Promise((resolve) => this.onboardingService.createExperience(formData).subscribe({
        next: (res: any) => {
          const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;
          if (newId) this.existingIds[index] = newId;
          group.markAsPristine();
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          this.checkStepStatus();
          resolve(true);
        },
        error: (err) => {
          console.error('Create failed', err);
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          resolve(false);
        }
      }));
    }
  }

  async saveData(): Promise<void> {
    if (this.savingInProgress) return;

    if (!this.experiencesForm.valid) {
      this.entries.controls.forEach((group: any) => group.markAllAsTouched());
      this.saveFailed.emit();
      return;
    }

    this.savingInProgress = true;
    const results = await Promise.all(this.entries.controls.map((_, index) => this.persistEntry(index)));
    this.savingInProgress = false;

    if (results.every(Boolean)) {
      this.stepSaved.emit();
    } else {
      this.saveFailed.emit();
    }
  }



  onFileUpload(event: any, index: number) {
    if (event.files && event.files.length > 0) {
      for (let file of event.files) {
        const check = validateFile(file);
        if (!check.valid) {
          this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
          continue;
        }
        this.processFile(file, index);
      }
    }
  }

  processFile(file: File, index: number) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const previewUrl = file.type === 'application/pdf'
        ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
        : e.target.result;

      const newItem: UploadedFileItem = { file, previewUrl, name: file.name, type: file.type, base64Data: e.target.result };

      const control = this.entries.at(index)?.get('cvFiles');
      if (control) {
        control.setValue([newItem]);
        control.markAsDirty();
        control.setErrors(null);
      }
    };
    reader.readAsDataURL(file);
  }

  triggerAddMore(index: number) {
    this.currentUploadIndex = index;
    if (this.hiddenFileInput) {
      this.hiddenFileInput.nativeElement.click();
    }
  }

  onHiddenFileChange(event: any) {
    const files = event.target.files;
    if (files && files.length > 0 && this.currentUploadIndex >= 0) {
      Array.from(files).forEach((file: any) => {
        this.processFile(file as File, this.currentUploadIndex);
      });
    }
    event.target.value = '';
  }

  removeFileObj(index: number) {
    const control = this.entries.at(index)?.get('cvFiles');
    if (control) {
      control.setValue([]);
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity();
    }
  }

  getUploadedFiles(index: number): UploadedFileItem[] {
    const val = this.entries.at(index)?.get('cvFiles')?.value;
    return Array.isArray(val) ? val : [];
  }

  getDocumentDisplayName(index: number): string {
    // For experiences, always label as Resume / CV when a file is present
    return 'Resume / CV';
  }

  get wantsToShare(): boolean {
    return this.experiencesForm.get('wantsToShare')?.value === true;
  }

  hasFiles(index: number): boolean {
    return this.getUploadedFiles(index).length > 0;
  }

  isPreviewLoading: boolean = false;

  onPreviewLoad() {
    this.isPreviewLoading = false;
  }

  openPreview(item: UploadedFileItem) {
    this.selectedPreviewItem = item;
    this.isPreviewLoading = true;
    this.safePdfUrl = null;

    if (item.type === 'application/pdf') {
      const url = item.previewUrl;
      if (typeof url === 'string') {
        if (url.startsWith('http')) {
          const urlStr = encodeURIComponent(url);
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://docs.google.com/viewer?url=${urlStr}&embedded=true`
          );
        } else {
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
      } else {
        this.safePdfUrl = url;
      }
    } else if (item?.type?.startsWith('image/')) {
      // Handled by <img> (load)
    } else {
      this.isPreviewLoading = false;
    }
    this.displayPreviewDialog = true;
  }
}